export type LivingBudgetBucket = "food" | "household" | "recurring";
export type HouseholdArea = "kitchen" | "bath" | "laundry" | "pet" | "other";
export type HouseholdStockStatus = "in_stock" | "low" | "out";

export interface HouseholdItem {
	itemId: string;
	name: string;
	area: HouseholdArea;
	status: HouseholdStockStatus;
	openedAt: string | null;
	finishedAt: string | null;
	lastPurchasedAt: string | null;
	lastUnitCost: number | null;
	purchaseCount: number;
	cycleDays: number[];
	avoidNext: boolean;
	sourceReceiptIds: string[];
	/** Receipt-line identity survives household display-name changes and refunds. */
	sourceReceiptLines?: Record<string, string[]>;
	updatedAt: string;
}

export interface ShoppingEntry {
	entryId: string;
	itemId: string | null;
	name: string;
	status: "pending" | "purchased";
	source: "manual" | "low" | "out";
	addedAt: string;
	purchasedAt: string | null;
	purchasedReceiptId: string | null;
	/** Keeps a reversible receipt link without claiming a later manual entry. */
	reopenedFromReceiptId?: string | null;
	purchaseStockBefore?: HouseholdItem | null;
	purchaseReopenedFromReceiptIdBefore?: string | null;
}

export interface LivingBudgetPlan {
	/** Discretionary period budget. Absent on legacy plans. */
	monthlySpendable?: number;
	period: string;
	monthStartDay: number;
	limits: Record<LivingBudgetBucket, number>;
	/** Absent on legacy explicit plans; new plans track which values the user set. */
	configuredBuckets?: LivingBudgetBucket[];
	/** #35: optional monthly limit for single categories such as 카페·배달. */
	categoryLimits?: Record<string, number>;
}

export interface HouseholdConsumptionState {
	schemaVersion: 1;
	items: HouseholdItem[];
	shopping: ShoppingEntry[];
	budgetPlans: LivingBudgetPlan[];
	recurringPayments?: RecurringPayment[];
}

export interface RecurringPayment {
	id: string;
	name: string;
	amount: number;
	dayOfMonth: number;
	categoryId: string;
	paused: boolean;
}

/** A date in the user's KST month, including short-month clamping. */
export function recurringPaymentDate(
	payment: RecurringPayment,
	anchor: Date,
): string {
	const kst = new Date(anchor.getTime() + 9 * 60 * 60 * 1000);
	const year = kst.getUTCFullYear();
	const month = kst.getUTCMonth();
	const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
	return `${year}-${String(month + 1).padStart(2, "0")}-${String(Math.min(last, payment.dayOfMonth)).padStart(2, "0")}`;
}

export function isRecurringPayment(value: unknown): value is RecurringPayment {
	if (!value || typeof value !== "object") return false;
	const item = value as Record<string, unknown>;
	return (
		typeof item.id === "string" &&
		item.id.length > 0 &&
		typeof item.name === "string" &&
		item.name.trim().length > 0 &&
		item.name.length <= 80 &&
		typeof item.amount === "number" &&
		Number.isSafeInteger(item.amount) &&
		item.amount > 0 &&
		typeof item.dayOfMonth === "number" &&
		Number.isInteger(item.dayOfMonth) &&
		item.dayOfMonth >= 1 &&
		item.dayOfMonth <= 31 &&
		typeof item.categoryId === "string" &&
		item.categoryId.length > 0 &&
		typeof item.paused === "boolean"
	);
}

export function recurringPaymentRecorded(
	paymentId: string,
	receipts: {
		recurringPaymentId?: string | null;
		transactedAt: string;
		deletedAt?: string | null;
	}[],
	anchor: Date,
): boolean {
	const month = recurringPaymentDate(
		{ dayOfMonth: 1 } as RecurringPayment,
		anchor,
	).slice(0, 7);
	return receipts.some(
		(receipt) =>
			!receipt.deletedAt &&
			receipt.recurringPaymentId === paymentId &&
			new Date(new Date(receipt.transactedAt).getTime() + 9 * 60 * 60 * 1000)
				.toISOString()
				.slice(0, 7) === month,
	);
}

export function hasLivingBudget(
	state: HouseholdConsumptionState,
	plan: LivingBudgetPlan,
): boolean {
	return state.budgetPlans.some(
		(candidate) =>
			candidate.period === plan.period &&
			(!candidate.configuredBuckets ||
				candidate.configuredBuckets.length === 3),
	);
}

export function isBudgetBucketSet(
	state: HouseholdConsumptionState,
	plan: LivingBudgetPlan,
	bucket: LivingBudgetBucket,
): boolean {
	const saved = state.budgetPlans.find(
		(candidate) => candidate.period === plan.period,
	);
	return Boolean(
		saved &&
			(!saved.configuredBuckets || saved.configuredBuckets.includes(bucket)),
	);
}

export function completeShopping(
	state: HouseholdConsumptionState,
	entryId: string,
	at: string,
): HouseholdConsumptionState {
	const entry = state.shopping.find(
		(candidate) =>
			candidate.entryId === entryId && candidate.status === "pending",
	);
	if (!entry) return state;
	const item =
		state.items.find((candidate) => candidate.itemId === entry.itemId) ?? null;
	const next = item
		? setHouseholdItemStatus(state, item.itemId, "in_stock", at)
		: state;
	return {
		...next,
		shopping: next.shopping.map((candidate) =>
			candidate.entryId === entryId
				? {
						...candidate,
						status: "purchased",
						purchasedAt: at,
						purchasedReceiptId: null,
						reopenedFromReceiptId: null,
						purchaseReopenedFromReceiptIdBefore:
							candidate.reopenedFromReceiptId ?? null,
						purchaseStockBefore: item,
					}
				: candidate,
		),
	};
}

export function undoShopping(
	state: HouseholdConsumptionState,
	entryId: string,
): HouseholdConsumptionState {
	const entry = state.shopping.find(
		(candidate) =>
			candidate.entryId === entryId && candidate.status === "purchased",
	);
	if (!entry || entry.purchasedReceiptId) return state;
	return {
		...state,
		items: state.items.map((item) =>
			item.itemId === entry.itemId &&
			item.updatedAt === entry.purchasedAt &&
			entry.purchaseStockBefore
				? entry.purchaseStockBefore
				: item,
		),
		shopping: state.shopping.map((candidate) =>
			candidate.entryId === entryId
				? {
						...candidate,
						status: "pending",
						purchasedAt: null,
						purchaseStockBefore: null,
						reopenedFromReceiptId:
							candidate.purchaseReopenedFromReceiptIdBefore ?? null,
					}
				: candidate,
		),
	};
}

export interface LivingExpense {
	categoryId: string;
	totalAmount: number;
	transactedAt: string;
}

export const DEFAULT_LIVING_BUDGETS: Record<LivingBudgetBucket, number> = {
	food: 450_000,
	household: 120_000,
	recurring: 180_000,
};

const HOME_ITEM_CATEGORY_IDS = new Set([
	"food-grocery",
	"daily-supermarket",
	"daily-hypermarket",
	"daily-household",
	"health-supplement",
	"beauty-cosmetics",
	"pet-food",
	"pet-supply",
]);

export function emptyHouseholdConsumption(): HouseholdConsumptionState {
	return { schemaVersion: 1, items: [], shopping: [], budgetPlans: [] };
}

export function livingBudgetBucket(
	categoryId: string,
): LivingBudgetBucket | null {
	if (
		categoryId.startsWith("food-") ||
		categoryId === "daily-supermarket" ||
		categoryId === "daily-hypermarket"
	)
		return "food";
	if (
		categoryId === "daily-household" ||
		categoryId.startsWith("pet-") ||
		categoryId === "beauty-cosmetics" ||
		categoryId === "health-supplement"
	)
		return "household";
	if (
		categoryId === "etc-recurring" ||
		categoryId === "home-utility" ||
		categoryId === "home-telecom" ||
		categoryId === "home-security" ||
		categoryId === "home-insurance" ||
		categoryId === "home-rental"
	)
		return "recurring";
	return null;
}

export function isHomeItemCategory(categoryId: string): boolean {
	return HOME_ITEM_CATEGORY_IDS.has(categoryId);
}

export function budgetPeriodFor(
	now: Date,
	monthStartDay: number,
): { key: string; start: Date; end: Date } {
	const safeStart = Math.min(28, Math.max(1, Math.trunc(monthStartDay)));
	const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
	let year = kst.getUTCFullYear();
	let month = kst.getUTCMonth();
	if (kst.getUTCDate() < safeStart) {
		month -= 1;
		if (month < 0) {
			month = 11;
			year -= 1;
		}
	}
	const start = new Date(Date.UTC(year, month, safeStart) - 9 * 60 * 60 * 1000);
	const end = new Date(
		Date.UTC(year, month + 1, safeStart) - 9 * 60 * 60 * 1000,
	);
	return {
		key: `${year}-${String(month + 1).padStart(2, "0")}`,
		start,
		end,
	};
}

export function resolveLivingBudgetPlan(
	state: HouseholdConsumptionState,
	now = new Date(),
	monthStartDay = 1,
): LivingBudgetPlan {
	const safeStart = Math.min(28, Math.max(1, Math.trunc(monthStartDay)));
	const period = budgetPeriodFor(now, safeStart).key;
	const saved = state.budgetPlans.find((plan) => plan.period === period);
	return saved
		? { ...saved, monthStartDay: safeStart }
		: {
				period,
				monthStartDay: safeStart,
				limits: { ...DEFAULT_LIVING_BUDGETS },
			};
}

export function calculateLivingBudget(
	expenses: LivingExpense[],
	plan: LivingBudgetPlan,
	now = new Date(),
) {
	const window = budgetPeriodFor(now, plan.monthStartDay);
	const spent: Record<LivingBudgetBucket, number> = {
		food: 0,
		household: 0,
		recurring: 0,
	};
	for (const expense of expenses) {
		const at = new Date(expense.transactedAt);
		const bucket = livingBudgetBucket(expense.categoryId);
		if (
			bucket &&
			Number.isFinite(at.getTime()) &&
			at >= window.start &&
			at < window.end
		)
			spent[bucket] += Math.max(0, expense.totalAmount);
	}
	const remaining = {
		food: plan.limits.food - spent.food,
		household: plan.limits.household - spent.household,
		recurring: plan.limits.recurring - spent.recurring,
	};
	return {
		period: window.key,
		spent,
		remaining,
		totalSpent: spent.food + spent.household + spent.recurring,
		totalLimit:
			plan.limits.food + plan.limits.household + plan.limits.recurring,
	};
}

function normalizeItemName(value: string): string {
	return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
}

function shoppingEntryFor(
	item: HouseholdItem,
	source: "low" | "out",
	at: string,
): ShoppingEntry {
	return {
		entryId: `item:${item.itemId}:${at}`,
		itemId: item.itemId,
		name: item.name,
		status: "pending",
		source,
		addedAt: at,
		purchasedAt: null,
		purchasedReceiptId: null,
	};
}

/** Older cycles reused one entry ID. Preserve all rows while giving each its own identity. */
export function normalizeShoppingEntryIds(
	state: HouseholdConsumptionState,
): HouseholdConsumptionState {
	const reserved = new Set(state.shopping.map((entry) => entry.entryId));
	const seen = new Set<string>();
	let changed = false;
	const shopping = state.shopping.map((entry) => {
		if (!seen.has(entry.entryId)) {
			seen.add(entry.entryId);
			return entry;
		}
		let suffix = 1;
		let id = `${entry.entryId}:cycle:${suffix}`;
		while (reserved.has(id) || seen.has(id))
			id = `${entry.entryId}:cycle:${++suffix}`;
		changed = true;
		seen.add(id);
		return { ...entry, entryId: id };
	});
	return changed ? { ...state, shopping } : state;
}

export function setHouseholdItemStatus(
	state: HouseholdConsumptionState,
	itemId: string,
	status: HouseholdStockStatus,
	at: string,
): HouseholdConsumptionState {
	const item = state.items.find((candidate) => candidate.itemId === itemId);
	if (!item) return state;
	if (item.status === status) return state;
	const elapsed =
		status === "out" && item.openedAt
			? Math.max(
					1,
					Math.round((Date.parse(at) - Date.parse(item.openedAt)) / 86_400_000),
				)
			: null;
	const updatedItem: HouseholdItem = {
		...item,
		status,
		openedAt: status === "in_stock" ? null : item.openedAt,
		finishedAt: status === "out" ? at : null,
		cycleDays:
			elapsed === null
				? item.cycleDays
				: [...item.cycleDays, elapsed].slice(-6),
		updatedAt: at,
	};
	let shopping = state.shopping;
	if ((status === "low" || status === "out") && !item.avoidNext) {
		const existing = shopping.find(
			(entry) => entry.itemId === itemId && entry.status === "pending",
		);
		if (!existing)
			shopping = [...shopping, shoppingEntryFor(updatedItem, status, at)];
	}
	if (status === "in_stock")
		shopping = shopping.map((entry) =>
			entry.itemId === itemId && entry.status === "pending"
				? { ...entry, status: "purchased", purchasedAt: at }
				: entry,
		);
	return {
		...state,
		items: state.items.map((candidate) =>
			candidate.itemId === itemId ? updatedItem : candidate,
		),
		shopping,
	};
}

export function itemCadenceDays(item: HouseholdItem): number | null {
	if (item.cycleDays.length < 2) return null;
	return Math.round(
		item.cycleDays.reduce((sum, days) => sum + days, 0) / item.cycleDays.length,
	);
}

export function addHouseholdItem(
	state: HouseholdConsumptionState,
	input: { itemId: string; name: string; area: HouseholdArea; at: string },
): HouseholdConsumptionState {
	const name = input.name.normalize("NFKC").trim().replace(/\s+/g, " ");
	if (!name) return state;
	const existing = state.items.find(
		(item) => normalizeItemName(item.name) === normalizeItemName(name),
	);
	if (existing)
		return setHouseholdItemStatus(state, existing.itemId, "in_stock", input.at);
	return {
		...state,
		items: [
			...state.items,
			{
				itemId: input.itemId,
				name,
				area: input.area,
				status: "in_stock",
				openedAt: null,
				finishedAt: null,
				lastPurchasedAt: null,
				lastUnitCost: null,
				purchaseCount: 0,
				cycleDays: [],
				avoidNext: false,
				sourceReceiptIds: [],
				updatedAt: input.at,
			},
		],
	};
}

export function addShoppingEntry(
	state: HouseholdConsumptionState,
	input: { entryId: string; name: string; at: string },
): HouseholdConsumptionState {
	const name = input.name.normalize("NFKC").trim().replace(/\s+/g, " ");
	if (!name) return state;
	if (
		state.shopping.some(
			(entry) =>
				entry.status === "pending" &&
				normalizeItemName(entry.name) === normalizeItemName(name),
		)
	)
		return state;
	return {
		...state,
		shopping: [
			...state.shopping,
			{
				entryId: input.entryId,
				itemId: null,
				name,
				status: "pending",
				source: "manual",
				addedAt: input.at,
				purchasedAt: null,
				purchasedReceiptId: null,
			},
		],
	};
}

function receiptLineMatches(
	item: HouseholdItem,
	receiptId: string,
	name: string,
): boolean {
	const key = normalizeItemName(name);
	return (
		Boolean(item.sourceReceiptLines?.[receiptId]?.includes(key)) ||
		item.itemId === `receipt:${receiptId}:${key}`
	);
}

export function reconcileReceiptItems(
	state: HouseholdConsumptionState,
	receipt: {
		receiptId: string;
		categoryId: string;
		transactedAt: string;
		totalAmount: number;
		items?: { name: string; amount: number | null; qty: number | null }[];
	},
): HouseholdConsumptionState {
	if (!isHomeItemCategory(receipt.categoryId) || !receipt.items?.length)
		return state;
	const receiptTime = Date.parse(receipt.transactedAt);
	if (!Number.isFinite(receiptTime)) return state;
	let next = state;
	for (const line of receipt.items) {
		const name = line.name.normalize("NFKC").trim().replace(/\s+/g, " ");
		if (!name) continue;
		let item =
			next.items.find((candidate) =>
				receiptLineMatches(candidate, receipt.receiptId, name),
			) ??
			next.items.find(
				(candidate) =>
					normalizeItemName(candidate.name) === normalizeItemName(name),
			);
		if (!item) {
			next = addHouseholdItem(next, {
				itemId: `receipt:${receipt.receiptId}:${normalizeItemName(name)}`,
				name,
				area: receipt.categoryId.startsWith("pet-") ? "pet" : "kitchen",
				at: receipt.transactedAt,
			});
			item = next.items.find(
				(candidate) =>
					normalizeItemName(candidate.name) === normalizeItemName(name),
			);
		}
		if (!item) continue;
		const linked = (item.sourceReceiptIds ?? []).includes(receipt.receiptId);
		const lineNames = [
			...new Set([
				...(item.sourceReceiptLines?.[receipt.receiptId] ?? []),
				normalizeItemName(name),
			]),
		];
		if (linked) {
			next = {
				...next,
				items: next.items.map((candidate) =>
					candidate.itemId === item?.itemId
						? {
								...candidate,
								sourceReceiptLines: {
									...candidate.sourceReceiptLines,
									[receipt.receiptId]: lineNames,
								},
							}
						: candidate,
				),
			};
			continue;
		}
		// A basket total cannot establish an individual item's price.
		const cost =
			typeof line.amount === "number" &&
			Number.isFinite(line.amount) &&
			line.amount >= 0 &&
			typeof line.qty === "number" &&
			Number.isFinite(line.qty) &&
			line.qty > 0
				? line.amount / line.qty
				: null;
		const updatesStock = receiptTime >= Date.parse(item.updatedAt);
		const updatesPurchase =
			!item.lastPurchasedAt || receiptTime >= Date.parse(item.lastPurchasedAt);
		next = {
			...next,
			items: next.items.map((candidate) =>
				candidate.itemId === item?.itemId
					? {
							...candidate,
							status: updatesStock ? "in_stock" : candidate.status,
							openedAt: updatesStock ? null : candidate.openedAt,
							finishedAt: updatesStock ? null : candidate.finishedAt,
							lastPurchasedAt: updatesPurchase
								? receipt.transactedAt
								: candidate.lastPurchasedAt,
							lastUnitCost: updatesPurchase ? cost : candidate.lastUnitCost,
							purchaseCount: candidate.purchaseCount + 1,
							sourceReceiptLines: {
								...candidate.sourceReceiptLines,
								[receipt.receiptId]: lineNames,
							},
							sourceReceiptIds: [
								...new Set([
									...(candidate.sourceReceiptIds ?? []),
									receipt.receiptId,
								]),
							],
							updatedAt: updatesStock
								? receipt.transactedAt
								: candidate.updatedAt,
						}
					: candidate,
			),
			shopping: next.shopping.map((entry) =>
				(entry.itemId === item?.itemId ||
					normalizeItemName(entry.name) === normalizeItemName(name)) &&
				entry.status === "pending" &&
				Date.parse(entry.addedAt) <= receiptTime
					? {
							...entry,
							status: "purchased",
							purchasedAt: receipt.transactedAt,
							purchasedReceiptId: receipt.receiptId,
						}
					: entry,
			),
		};
	}
	return next;
}

/**
 * Reopens only the shopping result that this receipt completed.  A refund does
 * not claim to know whether an item is physically still at home, so its manual
 * stock status is preserved for the owner to confirm.
 */
export function reverseReceiptItems(
	state: HouseholdConsumptionState,
	receipt: {
		receiptId: string;
		categoryId: string;
		items?: { name: string; amount: number | null; qty: number | null }[];
	},
): HouseholdConsumptionState {
	if (!isHomeItemCategory(receipt.categoryId) || !receipt.items?.length)
		return state;
	const names = new Set(
		receipt.items.map((line) => normalizeItemName(line.name)).filter(Boolean),
	);
	if (!names.size) return state;
	return {
		...state,
		items: state.items.map((item) => {
			if (!(item.sourceReceiptIds ?? []).includes(receipt.receiptId))
				return item;
			return {
				...item,
				purchaseCount: Math.max(0, item.purchaseCount - 1),
				sourceReceiptLines: {
					...item.sourceReceiptLines,
					[receipt.receiptId]:
						item.sourceReceiptLines?.[receipt.receiptId] ??
						(receipt.items ?? [])
							.filter(
								(line) =>
									normalizeItemName(line.name) ===
										normalizeItemName(item.name) ||
									receiptLineMatches(item, receipt.receiptId, line.name),
							)
							.map((line) => normalizeItemName(line.name)),
				},
				sourceReceiptIds: (item.sourceReceiptIds ?? []).filter(
					(id) => id !== receipt.receiptId,
				),
			};
		}),
		shopping: state.shopping.map((entry) =>
			entry.purchasedReceiptId === receipt.receiptId
				? {
						...entry,
						status: "pending" as const,
						purchasedAt: null,
						purchasedReceiptId: null,
						reopenedFromReceiptId: receipt.receiptId,
					}
				: entry,
		),
	};
}

/** Undo an immediately saved receipt using its in-memory before/after evidence.
 * Refunds deliberately use reverseReceiptItems alone. Only fields still equal to
 * the saved result are restored, so later edits and later receipts win. */
export function undoSavedReceiptStock(
	current: HouseholdConsumptionState,
	reversed: HouseholdConsumptionState,
	before: HouseholdConsumptionState,
	after: HouseholdConsumptionState,
	receiptIds: string[],
): HouseholdConsumptionState {
	return {
		...reversed,
		shopping: reversed.shopping.map((entry) => {
			const now = current.shopping.find(
				(candidate) => candidate.entryId === entry.entryId,
			);
			const old = before.shopping.find(
				(candidate) => candidate.entryId === entry.entryId,
			);
			if (
				!now?.purchasedReceiptId ||
				!receiptIds.includes(now.purchasedReceiptId) ||
				!old
			)
				return entry;
			return {
				...entry,
				reopenedFromReceiptId: old.reopenedFromReceiptId ?? null,
			};
		}),
		items: reversed.items.map((item) => {
			const old = before.items.find(
				(candidate) => candidate.itemId === item.itemId,
			);
			const saved = after.items.find(
				(candidate) => candidate.itemId === item.itemId,
			);
			const now = current.items.find(
				(candidate) => candidate.itemId === item.itemId,
			);
			const lines = { ...item.sourceReceiptLines };
			for (const id of receiptIds) {
				if (
					!saved?.sourceReceiptIds.includes(id) ||
					!now?.sourceReceiptIds.includes(id)
				)
					continue;
				if (old?.sourceReceiptLines?.[id])
					lines[id] = old.sourceReceiptLines[id];
				else delete lines[id];
			}
			const withoutUndoLines = { ...item, sourceReceiptLines: lines };
			if (
				!old ||
				!saved ||
				!now ||
				JSON.stringify(now.sourceReceiptIds) !==
					JSON.stringify(saved.sourceReceiptIds) ||
				now.updatedAt !== saved.updatedAt ||
				now.status !== saved.status ||
				now.openedAt !== saved.openedAt ||
				now.finishedAt !== saved.finishedAt ||
				now.lastPurchasedAt !== saved.lastPurchasedAt ||
				now.lastUnitCost !== saved.lastUnitCost
			)
				return withoutUndoLines;
			return {
				...withoutUndoLines,
				status: old.status,
				openedAt: old.openedAt,
				finishedAt: old.finishedAt,
				updatedAt: old.updatedAt,
				lastPurchasedAt: old.lastPurchasedAt,
				lastUnitCost: old.lastUnitCost,
			};
		}),
	};
}

/**
 * Reinstates only the exact receipt links that reverseReceiptItems recorded.
 * Later manual stock edits and shopping entries with the same name are not
 * receipt evidence and must remain untouched.
 */
export function restoreReceiptItems(
	state: HouseholdConsumptionState,
	receipt: {
		receiptId: string;
		categoryId: string;
		transactedAt: string;
		items?: { name: string; amount: number | null; qty: number | null }[];
	},
): HouseholdConsumptionState {
	if (!isHomeItemCategory(receipt.categoryId) || !receipt.items?.length)
		return state;
	const names = new Set(
		receipt.items.map((line) => normalizeItemName(line.name)).filter(Boolean),
	);
	if (!names.size) return state;
	return {
		...state,
		items: state.items.map((item) =>
			((receipt.items ?? []).some((line) =>
				receiptLineMatches(item, receipt.receiptId, line.name),
			) ||
				names.has(normalizeItemName(item.name))) &&
			!(item.sourceReceiptIds ?? []).includes(receipt.receiptId)
				? {
						...item,
						purchaseCount: item.purchaseCount + 1,
						sourceReceiptIds: [
							...(item.sourceReceiptIds ?? []),
							receipt.receiptId,
						],
					}
				: item,
		),
		shopping: state.shopping.map((entry) =>
			entry.reopenedFromReceiptId === receipt.receiptId
				? {
						...entry,
						status: "purchased" as const,
						purchasedAt: receipt.transactedAt,
						purchasedReceiptId: receipt.receiptId,
						reopenedFromReceiptId: null,
					}
				: entry,
		),
	};
}

export function setBudgetLimit(
	state: HouseholdConsumptionState,
	plan: LivingBudgetPlan,
	bucket: LivingBudgetBucket,
	value: number,
): HouseholdConsumptionState {
	const previous = state.budgetPlans.find(
		(candidate) => candidate.period === plan.period,
	);
	const updated = {
		...plan,
		limits: {
			...(previous?.limits ?? { food: 0, household: 0, recurring: 0 }),
			[bucket]: Math.max(0, Math.round(value)),
		},
		configuredBuckets: [
			...new Set([
				...(previous
					? (previous.configuredBuckets ??
						(["food", "household", "recurring"] as LivingBudgetBucket[]))
					: []),
				bucket,
			]),
		],
	};
	return {
		...state,
		budgetPlans: [
			...state.budgetPlans.filter(
				(candidate) => candidate.period !== plan.period,
			),
			updated,
		],
	};
}

/* ---------- 2026-10 완성 웨이브: 물건 고치기·지우기(#31), 분류 한도(#35) ---------- */

export type RenameResult =
	| { ok: true; state: HouseholdConsumptionState }
	| { ok: false; reason: "empty" | "duplicate"; duplicateItemId?: string };

export function renameHouseholdItem(
	state: HouseholdConsumptionState,
	itemId: string,
	rawName: string,
	at: string,
): RenameResult {
	const name = rawName
		.normalize("NFKC")
		.trim()
		.replace(/\s+/g, " ")
		.slice(0, 80);
	if (!name) return { ok: false, reason: "empty" };
	const duplicate = state.items.find(
		(item) =>
			item.itemId !== itemId &&
			normalizeItemName(item.name) === normalizeItemName(name),
	);
	if (duplicate)
		return {
			ok: false,
			reason: "duplicate",
			duplicateItemId: duplicate.itemId,
		};
	return {
		ok: true,
		state: {
			...state,
			items: state.items.map((item) =>
				item.itemId === itemId
					? {
							...item,
							name,
							updatedAt: at,
							sourceReceiptLines: {
								...item.sourceReceiptLines,
								...Object.fromEntries(
									(item.sourceReceiptIds ?? []).map((id) => [
										id,
										item.sourceReceiptLines?.[id] ?? [
											normalizeItemName(item.name),
										],
									]),
								),
							},
						}
					: item,
			),
			shopping: state.shopping.map((entry) =>
				entry.itemId === itemId && entry.status === "pending"
					? { ...entry, name }
					: entry,
			),
		},
	};
}

export function moveHouseholdItem(
	state: HouseholdConsumptionState,
	itemId: string,
	area: HouseholdArea,
	at: string,
): HouseholdConsumptionState {
	return {
		...state,
		items: state.items.map((item) =>
			item.itemId === itemId ? { ...item, area, updatedAt: at } : item,
		),
	};
}

/** Merges purchase history of `fromId` into `intoId` ("이미 있어요. 합칠까요?"). */
export function mergeHouseholdItems(
	state: HouseholdConsumptionState,
	fromId: string,
	intoId: string,
	at: string,
): HouseholdConsumptionState {
	const from = state.items.find((item) => item.itemId === fromId);
	const into = state.items.find((item) => item.itemId === intoId);
	if (!from || !into || fromId === intoId) return state;
	const latest = (a: string | null, b: string | null) =>
		!a ? b : !b ? a : Date.parse(a) >= Date.parse(b) ? a : b;
	const lastPurchasedAt = latest(into.lastPurchasedAt, from.lastPurchasedAt);
	const merged: HouseholdItem = {
		...into,
		purchaseCount: into.purchaseCount + from.purchaseCount,
		cycleDays: [...into.cycleDays, ...from.cycleDays].slice(-6),
		sourceReceiptLines: Object.fromEntries(
			[
				...new Set([
					...Object.keys(into.sourceReceiptLines ?? {}),
					...Object.keys(from.sourceReceiptLines ?? {}),
				]),
			].map((id) => [
				id,
				[
					...new Set([
						...(into.sourceReceiptLines?.[id] ?? []),
						...(from.sourceReceiptLines?.[id] ?? []),
					]),
				],
			]),
		),
		sourceReceiptIds: [
			...new Set([
				...(into.sourceReceiptIds ?? []),
				...(from.sourceReceiptIds ?? []),
			]),
		],
		lastPurchasedAt,
		lastUnitCost:
			lastPurchasedAt === from.lastPurchasedAt && from.lastUnitCost !== null
				? from.lastUnitCost
				: into.lastUnitCost,
		updatedAt: at,
	};
	const hasPending = state.shopping.some(
		(entry) => entry.itemId === intoId && entry.status === "pending",
	);
	return {
		...state,
		items: state.items
			.filter((item) => item.itemId !== fromId)
			.map((item) => (item.itemId === intoId ? merged : item)),
		shopping: state.shopping
			.filter(
				(entry) =>
					!(
						entry.itemId === fromId &&
						entry.status === "pending" &&
						hasPending
					),
			)
			.map((entry) =>
				entry.itemId === fromId
					? { ...entry, itemId: intoId, name: into.name }
					: entry,
			),
	};
}

/** Removes an item; pending shopping rows stay, just without the item link. */
export function removeHouseholdItem(
	state: HouseholdConsumptionState,
	itemId: string,
): HouseholdConsumptionState {
	return {
		...state,
		items: state.items.filter((item) => item.itemId !== itemId),
		shopping: state.shopping.map((entry) =>
			entry.itemId === itemId ? { ...entry, itemId: null } : entry,
		),
	};
}

export interface CategoryLimitStatus {
	categoryId: string;
	limit: number;
	spent: number;
	remaining: number;
	ratio: number;
}

export function setCategoryLimit(
	state: HouseholdConsumptionState,
	plan: LivingBudgetPlan,
	categoryId: string,
	value: number | null,
): HouseholdConsumptionState {
	const saved = state.budgetPlans.find(
		(candidate) => candidate.period === plan.period,
	);
	const base: LivingBudgetPlan = saved ?? {
		...plan,
		limits: { food: 0, household: 0, recurring: 0 },
		configuredBuckets: [],
	};
	const categoryLimits = { ...(base.categoryLimits ?? {}) };
	if (value === null || !Number.isSafeInteger(value) || value <= 0)
		delete categoryLimits[categoryId];
	else categoryLimits[categoryId] = Math.round(value);
	const updated: LivingBudgetPlan = { ...base, categoryLimits };
	return {
		...state,
		budgetPlans: [
			...state.budgetPlans.filter(
				(candidate) => candidate.period !== plan.period,
			),
			updated,
		],
	};
}

/**
 * Limits are per budget period; a new period carries the latest limits
 * forward so a monthly cafe limit does not vanish on payday.
 */
export function activeCategoryLimits(
	state: HouseholdConsumptionState,
	plan: LivingBudgetPlan,
): Record<string, number> {
	const own = state.budgetPlans.find(
		(candidate) => candidate.period === plan.period,
	);
	if (own?.categoryLimits) return own.categoryLimits;
	const latest = [...state.budgetPlans]
		.filter(
			(candidate) => candidate.categoryLimits && candidate.period < plan.period,
		)
		.sort((a, b) => b.period.localeCompare(a.period))[0];
	return latest?.categoryLimits ?? {};
}

export function calculateCategoryLimits(
	expenses: LivingExpense[],
	limits: Record<string, number>,
	monthStartDay: number,
	now = new Date(),
): CategoryLimitStatus[] {
	const window = budgetPeriodFor(now, monthStartDay);
	return Object.entries(limits).map(([categoryId, limit]) => {
		const spent = expenses
			.filter((expense) => {
				const at = Date.parse(expense.transactedAt);
				return (
					expense.categoryId === categoryId &&
					Number.isFinite(at) &&
					at >= window.start.getTime() &&
					at < window.end.getTime()
				);
			})
			.reduce((sum, expense) => sum + Math.max(0, expense.totalAmount), 0);
		return {
			categoryId,
			limit,
			spent,
			remaining: limit - spent,
			ratio: limit ? spent / limit : 0,
		};
	});
}
